import React, { useState } from 'react';
import { Link } from "react-router-dom";
import AdminLayout from 'Pages/Admin/AdminLayout';
import { PrimaryButton, SecondaryButton } from 'Components/Button';
import { ModalTrigger } from 'Components/Modal';
import {FormHandler, Input, BooleanSelect, Select, Checkbox} from 'Components/Form';
import { Alert, Loading } from 'Components/Partials';
import AdminUsersApi from 'Services/Api/Admin/Users';
import AdminGroupsApi from 'Services/Api/Admin/Groups';
import AdminExhibitorsApi from 'Services/Api/Admin/Exhibitors';
import AdminWaitlistApi from 'Services/Api/Admin/Waitlist';
import Registration from "Pages/SitePages/Registration";
import AttendanceStatusModal from './AttendanceStatusModal';

const ATTENDANCE_STATUS_LABELS = {
    confirmed_in_person: 'Confirmed In-Person',
    external_waitlist:   'External Waitlist',
    internal_waitlist:   'Internal Waitlist',
    virtual:             'Virtual',
    cancelled:           'Cancelled',
};

class UsersStore extends React.Component {
    success = 'User ' + (this.props.match?.params?.user ? 'updated' : 'created') + ' successfully';

    state = {
        user_id: this.props.match?.params?.user ?? '',
        exhibitors: null,
        capacity: null,
        showRegistrationForm: false,
        showOtherElements: false,
    };

    componentDidMount = async () => {
        if (this.state.user_id) {
            this.loadUser();
        } else {
            this.props.setInitialValues({
                is_admin: false,
                enabled: true,
                automatically_verify_email: false,
                exhibitor_id: '',
                group_id: null,
                send_user_invite: false,
                send_password_reset_link: false,
            });
        }

        await this.loadExhibitors();
        await this.loadCapacity();
    }

    loadUser = async () => {
        const { user_id } = this.state;
        const response = await AdminUsersApi.get(user_id);
        let group_ids = response.data.data.groups.map(g => g.id);

        if (!window.base.features.allow_multiple_groups_per_user) {
            group_ids = group_ids[0];
        }

        this.props.setForm({
            ...response.data.data,
            afternoon_session_acknowledgement: response.data.data.afternoon_session_acknowledgement !== undefined
                ? response.data.data.afternoon_session_acknowledgement
                : false,
            group_ids
        });
    }

    /**
     * Capacity is only needed to know whether the in-person option can still be offered,
     * so a failed load leaves it null and the api stays the backstop.
     */
    loadCapacity = async () => {
        if (!this.state.user_id) {
            return;
        }

        const response = await AdminWaitlistApi.index();

        this.setState({
            capacity: response?.data?.capacity ?? null,
        });
    }

    /**
     * The status is patched straight into the form rather than reloading the user, so any
     * unsaved edits on the rest of the form survive the change.
     */
    handleAttendanceUpdated = async (status) => {
        this.props.handleInput('attendance_status', status);

        await this.loadCapacity();
    }

    loadExhibitors = async () => {
        const response = await AdminExhibitorsApi.get(null, {
            ignorePagination: true
        });

        let exhibitors = response.data.data.map((exhibitor, key) => {
            return {
                label: exhibitor.name,
                value: exhibitor.id
            };
        });

        exhibitors.unshift({ label: 'None', value: '' });

        this.setState({
            exhibitors,
        });
    }

    handleSubmit = (form) => {
        const { user_id } = this.state;

        if (user_id) {
            return AdminUsersApi.patch(user_id, form);
        } else {
            return AdminUsersApi.post(null, form);
        }
    };

    toggleSection = (section) => {
        this.setState((prevState) => {
            const newState = {
                showRegistrationForm: false,
                showOtherElements: false,
                afternoon_session_acknowledgement: false
            };
            newState[section] = !prevState[section];
            return newState;
        });
    }

    render() {
        const { user_id, exhibitors, capacity, showRegistrationForm, showOtherElements } = this.state;
        const { form, working, alert, setForm, handleInput, getFieldError, handleSubmit } = this.props;

        return (
            <AdminLayout title={'Users - ' + (user_id ? 'Update' : 'Create') + ' User'}>
                <div className={"flex justify-center gap-4 px-6 py-4"}>
                    <PrimaryButton onClick={() => this.toggleSection('showRegistrationForm')} text={"Register User"} />
                    <PrimaryButton onClick={() => this.toggleSection('showOtherElements')} text="Add/Edit User" />

                    {user_id &&
                        <ModalTrigger
                            component={AttendanceStatusModal}
                            props={{
                                user: { ...form, id: user_id },
                                capacity,
                                onUpdated: this.handleAttendanceUpdated,
                            }}
                        >
                            <PrimaryButton type="button" text="Update Attendance" />
                        </ModalTrigger>
                    }
                </div>

                {showRegistrationForm && <Registration isAdmin="true" />}

                {showOtherElements && (
                    <form
                        className="divide-y divide-gray-200"
                        onSubmit={(e) => handleSubmit(e, this.handleSubmit, this.success, (user_id ? false : true))}
                    >
                        <div className="mx-6">
                            {alert !== null && (<Alert {...alert} />)}

                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <Input
                                    label="First Name"
                                    value={form.first_name}
                                    id="first_name"
                                    error={getFieldError('first_name')}
                                    onChange={(v) => handleInput('first_name', v)}
                                />

                                <Input
                                    label="Last Name"
                                    value={form.last_name}
                                    id="last_name"
                                    error={getFieldError('last_name')}
                                    onChange={(v) => handleInput('last_name', v)}
                                />

                                {user_id &&
                                    <div>
                                        <span className="block text-sm font-medium text-gray-700 mb-1">
                                            Attendance Type
                                        </span>
                                        <p className="text-sm text-gray-900 py-2">
                                            {ATTENDANCE_STATUS_LABELS[form.attendance_status] ?? form.attendance_status ?? 'Not registered'}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Change this with "Update Attendance" above.
                                        </p>
                                    </div>
                                }

                                <Checkbox
                                    label="Afternoon Session Only"
                                    id="afternoon_session_acknowledgement"
                                    value={form?.afternoon_session_acknowledgement ?? false}
                                    onChange={v => handleInput('afternoon_session_acknowledgement', v)}
                                    error={getFieldError('afternoon_session_acknowledgement')}
                                />

                                <Input
                                    label="Email"
                                    value={form.email}
                                    id="email"
                                    error={getFieldError('email')}
                                    onChange={(v) => handleInput('email', v)}
                                />

                                <div>
                                    <Select
                                        label="Exhibitor Editor"
                                        id="exhibitor_id"
                                        value={form.exhibitor_id}
                                        error={getFieldError('exhibitor_id')}
                                        onChange={v => handleInput('exhibitor_id', v)}
                                        options={exhibitors}
                                    />
                                </div>

                                <Input
                                    labelColour={ "text-app-blue" }
                                    label={"The Symposium will be presented in English, if you are not fluent in English what would be your preferred language?"}
                                    placeholder="Preferred Language"
                                    value={ form?.preferred_language }
                                    id="preferred_language"
                                    error={ getFieldError ( 'preferred_language' ) }
                                    onChange={ ( v ) => handleInput ( 'preferred_language', v ) }
                                    containerClassName={ "col-span-2 md:col-span-1" }
                                />
                                {!user_id &&
                                    <>
                                        <BooleanSelect
                                            label="Send User Invite Email"
                                            value={form.send_user_invite}
                                            id="send_user_invite"
                                            error={getFieldError('send_user_invite')}
                                            onChange={(v) => {
                                                if (v === false) {
                                                    setForm({
                                                        ...form,
                                                        send_user_invite: false,
                                                        send_password_reset_link: false
                                                    });
                                                } else {
                                                    handleInput('send_user_invite', v);
                                                }
                                            }}
                                        />

                                        {(form.is_admin || !window.base.using_hard_password) &&
                                            <>
                                                {form.send_user_invite &&
                                                    <BooleanSelect
                                                        label="Send User Invite Email With Secure Link To Set Password"
                                                        value={form.send_password_reset_link}
                                                        id="send_password_reset_link"
                                                        error={getFieldError('send_password_reset_link')}
                                                        onChange={(v) => handleInput('send_password_reset_link', v)}
                                                    />
                                                }

                                                {!form.send_password_reset_link &&
                                                    <>
                                                        <Input
                                                            label="Password"
                                                            type="password"
                                                            value={form.password}
                                                            id="password"
                                                            error={getFieldError('password')}
                                                            onChange={v => handleInput('password', v)}
                                                        />

                                                        <Input
                                                            label="Password Confirmation"
                                                            type="password"
                                                            value={form.password_confirmation}
                                                            id="password_confirmation"
                                                            error={getFieldError('password_confirmation')}
                                                            onChange={v => handleInput('password_confirmation', v)}
                                                        />
                                                    </>
                                                }
                                            </>
                                        }

                                        {window.base.features.verify_registrations &&
                                            <div className="my-auto">
                                                <BooleanSelect
                                                    label="Automatically Verify Email"
                                                    value={form.automatically_verify_email}
                                                    id="automatically_verify_email"
                                                    error={getFieldError('automatically_verify_email')}
                                                    onChange={(v) => handleInput('automatically_verify_email', v)}
                                                />
                                            </div>
                                        }
                                    </>
                                }

                                <BooleanSelect
                                    label="Admin"
                                    value={form.is_admin}
                                    id="is_admin"
                                    error={getFieldError('is_admin')}
                                    onChange={(v) => handleInput('is_admin', v)}
                                />

                                {window.base.features.groups &&
                                    <Select
                                        label="Group"
                                        id="group_ids"
                                        value={form.group_ids}
                                        error={getFieldError('group_ids')}
                                        onChange={(v) => handleInput('group_ids', v)}
                                        isAsync
                                        searchCallback={(data) => AdminGroupsApi.get(null, {
                                            ...data,
                                            hasNoTeam: true
                                        })}
                                        searchLabelKey="name"
                                        allowNull={!window.base.features.user_must_belong_to_a_group || form.is_admin}
                                        isMulti={window.base.features.allow_multiple_groups_per_user}
                                        key={form.is_admin}
                                    />
                                }

                                {window.base.features.user_can_invite &&
                                    <>
                                        <Input
                                            label="Number of Invites"
                                            value={form.number_of_invites}
                                            id="number_of_invites"
                                            error={getFieldError('number_of_invites')}
                                            onChange={(v) => handleInput('number_of_invites', v)}
                                            type="number"
                                        />

                                        <Select
                                            label="Invited By"
                                            id="invited_by"
                                            value={form.invited_by}
                                            error={getFieldError('invited_by')}
                                            onChange={(v) => handleInput('invited_by', v)}
                                            isAsync
                                            searchCallback={(data) => AdminUsersApi.get(null, data)}
                                            searchLabelKey={(record) => record?.full_name ?? record?.email}
                                            allowNull={true}
                                        />
                                    </>
                                }

                                <BooleanSelect
                                    label="Enabled"
                                    value={form.enabled}
                                    id="enabled"
                                    error={getFieldError('enabled')}
                                    onChange={(v) => handleInput('enabled', v)}
                                />
                            </div>

                            {window.base.features.subscriptions && !user_id &&
                                <div className="mb-4">
                                    <CashierSubscriptionSelector
                                        form={form}
                                        handleInput={handleInput}
                                        getFieldError={getFieldError}
                                        admin={true}
                                        collectPaymentMethod={true}
                                        group_ids={form.group_ids}
                                    />
                                </div>
                            }
                        </div>

                        <div className="px-6 pt-6 flex justify-end gap-4">
                            <Link to="/admin/users">
                                <SecondaryButton type="button" text="Back" />
                            </Link>

                            <PrimaryButton
                                text="Save"
                                working={working}
                            />
                        </div>
                    </form>
                )}
            </AdminLayout>
        )
    }
}

export default FormHandler(UsersStore);
